import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService, User, UserUpdate } from '../../service/user.service';
import { AuthService } from '../../service/auth.service';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { RingoverWidgetComponent } from '../ringover-widget/ringover-widget.component';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [
    CommonModule, 
    RingoverWidgetComponent,
    ReactiveFormsModule, 
    FormsModule,
    TableModule, 
    ButtonModule, 
    CardModule,
    MessageModule,
    MessagesModule,
    DialogModule,
    InputTextModule,
    CheckboxModule,
    DropdownModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css']
})
export class UserComponent implements OnInit {
  users: User[] = [];
  filteredUsers: User[] = [];
  selectedUsers: User[] = [];
  error = '';
  successMessage = '';
  loading = false;
  addingUser = false;
  updatingUser = false;
  
  // Filtres et recherche
  searchTerm = '';
  selectedStatus: string | null = null;
  statusFilter = [
    { label: 'Tous', value: null },
    { label: 'Actifs', value: 'active' },
    { label: 'Suspendus', value: 'suspended' }
  ];
  
  // Dialogs
  showAddUserDialog = false;
  showUserDetailDialog = false;
  showEditUserDialog = false;
  addUserForm: FormGroup;
  editUserForm: FormGroup;
  selectedUser: User | null = null;
  
  // Options pour les rôles
  roleOptions = [
    { label: 'Utilisateur', value: 'user' },
    { label: 'Administrateur', value: 'admin' },
  ];
  
  // Actions groupées
  bulkActionItems = [
    {
      label: 'Activer sélectionnés',
      icon: 'pi pi-check',
      command: () => this.bulkActivate()
    },
    {
      label: 'Suspendre sélectionnés',
      icon: 'pi pi-ban',
      command: () => this.bulkSuspend()
    },
    {
      label: 'Supprimer sélectionnés',
      icon: 'pi pi-trash',
      command: () => this.bulkDelete()
    }
  ];

  constructor(
    private userService: UserService,
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthService 
  ) {
    // Formulaire d'ajout d'utilisateur
    this.addUserForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      telephone: [''],
      password: ['', [Validators.required, Validators.minLength(6)]],
      hubspot_id: [''],
      role: ['user', Validators.required],
      is_active: [true]
    });

    // Formulaire d'édition d'utilisateur
    this.editUserForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      telephone: [''],
      password: [''], // Optionnel pour l'édition
      hubspot_id: [''],
      role: ['user', Validators.required],
      is_active: [true]
    });
  }
getInitials(name: string): string {
  if (!name) return 'U';
  const words = name.split(' ');
  return words.length > 1 
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}
  ngOnInit() {
    this.loadUsers();
  }

  // Méthodes utilitaires pour le formatage
  formatDate(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return 'Non disponible';
    
    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
      
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date invalide';
    }
  }

  getUsername(user: User): string {
    return user.username || user.email.split('@')[0] || 'Inconnu';
  }

  getRoleLabel(role: string): string {
    const roleMap: { [key: string]: string } = {
      'user': 'Utilisateur',
      'admin': 'Administrateur',
      'USER': 'Utilisateur',
      'ADMIN': 'Administrateur'
    };
    return roleMap[role] || role;
  }

  getRoleSeverity(role: string): string {
    const severityMap: { [key: string]: string } = {
      'admin': 'danger',
      'ADMIN': 'danger',
      'user': 'info',
      'USER': 'info'
    };
    return severityMap[role] || 'info';
  }

  getCreatedDate(user: User): string | Date | null {
    return user.createdAt || null;
  }

  // Gestion des utilisateurs
  loadUsers(): void {
    this.loading = true;
    this.error = '';
    
    this.userService.getUsers().subscribe({
      next: (data: User[]) => {
        console.log('Données utilisateurs reçues:', data);
        this.users = data || [];
        this.filteredUsers = [...this.users];
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Erreur lors du chargement:', err);
        this.error = err.error?.detail || 'Impossible de charger la liste des utilisateurs. Veuillez réessayer.';
        this.loading = false;
        this.users = [];
        this.filteredUsers = [];
      }
    });
  }

    filterUsers(): void {
    if (!this.users) {
      this.filteredUsers = [];
      return;
    }

    let filtered = [...this.users];
    
    // Filtre par recherche
    if (this.searchTerm && this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        const username = user.username || '';
        const email = user.email || '';
        return username.toLowerCase().includes(searchLower) || 
               email.toLowerCase().includes(searchLower);
      });
    }
    
    // Filtre par statut
    if (this.selectedStatus === 'active') {
      filtered = filtered.filter(user => user.is_active === true);
    } else if (this.selectedStatus === 'suspended') {
      filtered = filtered.filter(user => user.is_active === false);
    }
    
    this.filteredUsers = filtered;
  }

  // Méthode alternative pour le changement de recherche
  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.filterUsers();
  }

  // Méthode pour le changement de statut
  onStatusChange(): void {
    this.filterUsers();
  }


  // Actions sur les utilisateurs
  toggleUserStatus(user: User) {
    if (user.is_active) {
      this.suspend(user);
    } else {
      this.activate(user);
    }
  }

  suspend(user: User) {
    this.userService.suspendUser(user.id).subscribe({
      next: () => {
        this.showSuccess('Utilisateur suspendu avec succès');
        this.loadUsers();
      },
      error: (err) => {
        this.error = err.error?.detail || "Impossible de suspendre cet utilisateur. Veuillez réessayer.";
      }
    });
  }

  activate(user: User) {
    this.userService.activateUser(user.id).subscribe({
      next: () => {
        this.showSuccess('Utilisateur activé avec succès');
        this.loadUsers();
      },
      error: (err) => {
        this.error = err.error?.detail || "Impossible d'activer cet utilisateur. Veuillez réessayer.";
      }
    });
  }

  confirmDelete(user: User) {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.email} ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.delete(user);
      }
    });
  }

  delete(user: User) {
    this.userService.deleteUser(user.id).subscribe({
      next: () => {
        this.showSuccess('Utilisateur supprimé avec succès');
        this.loadUsers();
      },
      error: (err) => {
        this.error = err.error?.detail || "Impossible de supprimer cet utilisateur. Veuillez réessayer.";
      }
    });
  }

  // Ajout d'utilisateur
  addUser() {
    if (this.addUserForm.valid) {
      this.addingUser = true;
      
      const userData = {
        username: this.addUserForm.value.username,
        email: this.addUserForm.value.email,
        telephone: this.addUserForm.value.telephone?.trim() || null,
        password: this.addUserForm.value.password,
        hubspot_id: this.addUserForm.value.hubspot_id?.trim() || null,
        role: this.addUserForm.value.role
      };
      
      this.authService.register(userData).subscribe({
        next: () => {
          this.showSuccess('Utilisateur ajouté avec succès');
          this.showAddUserDialog = false;
          this.addUserForm.reset({ 
            role: 'user',
            is_active: true,
            hubspot_id: ''
          });
          this.loadUsers();
          this.addingUser = false;
        },
        error: (err: any) => {
          if (err.error?.detail?.includes('hubspot_id')) {
            this.error = "Cet ID HubSpot est déjà utilisé. Veuillez en choisir un autre ou laisser le champ vide.";
          } else if (err.error?.detail?.includes('Email déjà utilisé')) {
            this.error = "Cet email est déjà utilisé.";
          } else if (err.error?.detail?.includes('Nom d\'utilisateur déjà pris')) {
            this.error = "Ce nom d'utilisateur est déjà pris.";
          } else {
            this.error = err.error?.detail || err.error?.message || "Impossible de créer cet utilisateur. Veuillez réessayer.";
          }
          this.addingUser = false;
        }
      });
    }
  }

  // Voir les détails d'un utilisateur
  viewUserDetails(user: User) {
    this.userService.getUserDetail(user.id).subscribe({
      next: (userData) => {
        this.selectedUser = userData;
        this.showUserDetailDialog = true;
      },
      error: (err: any) => {
        this.error = err.error?.detail || 'Impossible de charger les détails de cet utilisateur. Veuillez réessayer.';
      }
    });
  }

  // Édition d'utilisateur
  editUser(user: User) {
    this.selectedUser = user;
    
    this.editUserForm.patchValue({
      username: user.username,
      email: user.email,
      telephone: user.telephone || '',
      hubspot_id: user.hubspot_id || '',
      role: user.role,
      is_active: user.is_active,
      password: ''
    });
    
    this.showEditUserDialog = true;
  }

  updateUser() {
    if (this.editUserForm.valid && this.selectedUser) {
      this.updatingUser = true;
      
      const updateData: UserUpdate = {
        username: this.editUserForm.value.username,
        email: this.editUserForm.value.email,
        telephone: this.editUserForm.value.telephone?.trim() || null,
        hubspot_id: this.editUserForm.value.hubspot_id?.trim() || null,
        role: this.editUserForm.value.role,
        is_active: this.editUserForm.value.is_active
      };

      // Ajouter le mot de passe seulement s'il est fourni
      if (this.editUserForm.value.password?.trim()) {
        updateData.password = this.editUserForm.value.password;
      }

      this.userService.updateUser(this.selectedUser.id, updateData).subscribe({
        next: () => {
          this.showSuccess('Utilisateur modifié avec succès');
          this.showEditUserDialog = false;
          this.selectedUser = null;
          this.editUserForm.reset();
          this.loadUsers();
          this.updatingUser = false;
        },
        error: (err: any) => {
          if (err.error?.detail?.includes('Email déjà utilisé')) {
            this.error = 'Cet email est déjà utilisé par un autre utilisateur';
          } else if (err.error?.detail?.includes('Nom d\'utilisateur déjà pris')) {
            this.error = 'Ce nom d\'utilisateur est déjà pris';
          } else if (err.error?.detail?.includes('HubSpot ID déjà utilisé')) {
            this.error = 'Cet ID HubSpot est déjà utilisé';
          } else {
            this.error = err.error?.detail || 'Impossible de modifier cet utilisateur. Veuillez réessayer.';
          }
          this.updatingUser = false;
        }
      });
    }
  }

  // Actions groupées
  bulkActivate() {
    if (this.selectedUsers.length === 0) {
      this.error = 'Aucun utilisateur sélectionné';
      return;
    }
    
    this.confirmationService.confirm({
      message: `Activer ${this.selectedUsers.length} utilisateur(s) sélectionné(s) ?`,
      header: 'Activation groupée',
      icon: 'pi pi-question-circle',
      accept: () => {
        const inactiveUsers = this.selectedUsers.filter(user => !user.is_active);
        let completed = 0;
        
        if (inactiveUsers.length === 0) {
          this.error = 'Tous les utilisateurs sélectionnés sont déjà actifs';
          return;
        }

        inactiveUsers.forEach(user => {
          this.userService.activateUser(user.id).subscribe({
            next: () => {
              completed++;
              if (completed === inactiveUsers.length) {
                this.showSuccess(`${completed} utilisateur(s) activé(s) avec succès`);
                this.loadUsers();
                this.selectedUsers = [];
              }
            },
            error: (err) => {
              this.error = `Erreur lors de l'activation de ${user.email}: ${err.error?.detail}`;
            }
          });
        });
      }
    });
  }

  bulkSuspend() {
    if (this.selectedUsers.length === 0) {
      this.error = 'Aucun utilisateur sélectionné';
      return;
    }
    
    this.confirmationService.confirm({
      message: `Suspendre ${this.selectedUsers.length} utilisateur(s) sélectionné(s) ?`,
      header: 'Suspension groupée',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        const activeUsers = this.selectedUsers.filter(user => user.is_active);
        let completed = 0;
        
        if (activeUsers.length === 0) {
          this.error = 'Tous les utilisateurs sélectionnés sont déjà suspendus';
          return;
        }

        activeUsers.forEach(user => {
          this.userService.suspendUser(user.id).subscribe({
            next: () => {
              completed++;
              if (completed === activeUsers.length) {
                this.showSuccess(`${completed} utilisateur(s) suspendu(s) avec succès`);
                this.loadUsers();
                this.selectedUsers = [];
              }
            },
            error: (err) => {
              this.error = `Erreur lors de la suspension de ${user.email}: ${err.error?.detail}`;
            }
          });
        });
      }
    });
  }

  bulkDelete() {
    if (this.selectedUsers.length === 0) {
      this.error = 'Aucun utilisateur sélectionné';
      return;
    }
    
    this.confirmationService.confirm({
      message: `Supprimer définitivement ${this.selectedUsers.length} utilisateur(s) sélectionné(s) ? Cette action est irréversible.`,
      header: 'Suppression groupée',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        let completed = 0;
        const totalUsers = this.selectedUsers.length;

        this.selectedUsers.forEach(user => {
          this.userService.deleteUser(user.id).subscribe({
            next: () => {
              completed++;
              if (completed === totalUsers) {
                this.showSuccess(`${completed} utilisateur(s) supprimé(s) avec succès`);
                this.loadUsers();
                this.selectedUsers = [];
              }
            },
            error: (err) => {
              this.error = `Erreur lors de la suppression de ${user.email}: ${err.error?.detail}`;
            }
          });
        });
      }
    });
  }

  // Méthode utilitaire pour afficher les messages de succès
  private showSuccess(message: string) {
    this.messageService.add({
      severity: 'success',
      summary: 'Succès',
      detail: message,
      life: 5000
    });
    
    // Fallback avec propriété pour compatibilité
    this.successMessage = message;
    setTimeout(() => this.successMessage = '', 5000);
  }

  // Méthode pour fermer les dialogs et nettoyer
  closeUserDetailDialog() {
    this.showUserDetailDialog = false;
    this.selectedUser = null;
  }

  closeEditUserDialog() {
    this.showEditUserDialog = false;
    this.selectedUser = null;
    this.editUserForm.reset();
  }

  closeAddUserDialog() {
    this.showAddUserDialog = false;
    this.addUserForm.reset({
      role: 'user',
      is_active: true
    });
  }
}
